import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Mail, Lock, User, Loader2, Users, ChevronDown } from 'lucide-react';
import IncomeFlowHero from './IncomeFlowHero';

function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Team mode fields
  const [enableTeamMode, setEnableTeamMode] = useState(false);
  const [role, setRole] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [availableLeaders, setAvailableLeaders] = useState([]);
  const [loadingLeaders, setLoadingLeaders] = useState(false);
  
  const { login, signup } = useAuth();

  // Load available leaders when role changes
  useEffect(() => {
    if (!isLogin && enableTeamMode && role && role !== 'satellite') {
      loadLeaders();
    } else {
      setAvailableLeaders([]);
      setLeaderId('');
    }
  }, [role, enableTeamMode, isLogin]);

  const loadLeaders = async () => {
    try {
      setLoadingLeaders(true);
      // We need a temporary token to call the API, or we can make this endpoint public
      // For now, we'll fetch leaders without authentication
      const response = await axios.get(`${API}/leaders/list?role=${role}`);
      setAvailableLeaders(response.data);
    } catch (err) {
      console.error('Error loading leaders:', err);
      setAvailableLeaders([]);
    } finally {
      setLoadingLeaders(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        if (!name.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        
        // Validate team mode fields
        if (enableTeamMode) {
          if (!role) {
            setError('Please select your role');
            setLoading(false);
            return;
          }
          if (role !== 'satellite' && !leaderId) {
            setError('Please select your leader');
            setLoading(false);
            return;
          }
        }
        
        const signupRole = enableTeamMode ? role : null;
        const signupLeaderId = enableTeamMode && role !== 'satellite' ? leaderId : null;
        
        result = await signup(name, email, password, signupRole, signupLeaderId);
      }

      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Hero Positioning */}
        <div className="text-center mb-6 sm:mb-8 mt-6 sm:mt-12">
          <div className="flex items-center justify-center">
            <img src="/logo.png" alt="Paper Trail Logo" className="h-24 sm:h-32 md:h-40 w-auto" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-2">
            Run your finances like a system.
          </h1>
          <p className="text-gray-300 text-sm sm:text-base mt-3 max-w-sm mx-auto leading-relaxed">
            Route every dollar through your income hub, automatically allocate it across Personal, Business, Taxes, and Savings, and always know the weekly number you need to hit.
          </p>
        </div>

        {/* Hero animation */}
        <div className="mb-6">
          <IncomeFlowHero />
        </div>

        {/* 3-step explainer */}
        <div className="mb-6 grid grid-cols-1 gap-2 text-left">
          <div className="flex items-start gap-3 bg-gray-800/60 border border-gray-700 rounded-lg p-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
            <p className="text-sm text-gray-200">Route income into your Business Account.</p>
          </div>
          <div className="flex items-start gap-3 bg-gray-800/60 border border-gray-700 rounded-lg p-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">2</span>
            <p className="text-sm text-gray-200">Automatically allocate it across your 4-account system.</p>
          </div>
          <div className="flex items-start gap-3 bg-gray-800/60 border border-gray-700 rounded-lg p-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">3</span>
            <p className="text-sm text-gray-200">Hit your weekly target and rebalance when needed.</p>
          </div>
        </div>

        {/* Auth Card */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              {isLogin ? 'Welcome back' : 'Build your system'}
            </h2>
            <p className="text-gray-300 text-sm">
              {isLogin
                ? 'Sign in to your financial operating system.'
                : 'Create an account to start routing, allocating, and hitting your number.'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
              {!isLogin && (
                <p className="mt-1 text-xs text-gray-400">Minimum 6 characters</p>
              )}
            </div>

            {/* Team Mode Section */}
            {!isLogin && (
              <div className="border-t border-gray-600 pt-4">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="teamMode"
                    checked={enableTeamMode}
                    onChange={(e) => {
                      setEnableTeamMode(e.target.checked);
                      if (!e.target.checked) {
                        setRole('');
                        setLeaderId('');
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="teamMode" className="ml-2 text-sm font-medium text-gray-200 flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    Join as team member (optional)
                  </label>
                </div>

                {enableTeamMode && (
                  <div className="space-y-4 ml-6 pl-4 border-l-2 border-blue-500">
                    {/* Role Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-2">
                        Select Your Role *
                      </label>
                      <div className="relative">
                        <select
                          value={role}
                          onChange={(e) => {
                            setRole(e.target.value);
                            setLeaderId('');
                          }}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none"
                          required={enableTeamMode}
                        >
                          <option value="">-- Select Role --</option>
                          <option value="satellite">Satellite (Top Level - Chad Douglas)</option>
                          <option value="fsl">Field Sales Leader (FSL)</option>
                          <option value="fta">Field Training Agent (FTA)</option>
                          <option value="agent">Agent (Writing Agent)</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                      <p className="mt-1 text-xs text-gray-400">
                        {role === 'satellite' && 'You oversee all FSLs, FTAs, and Agents'}
                        {role === 'fsl' && 'You oversee FTAs and their Agents'}
                        {role === 'fta' && 'You oversee Agents'}
                        {role === 'agent' && 'You report to an FTA'}
                      </p>
                    </div>

                    {/* Leader Selection */}
                    {role && role !== 'satellite' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">
                          Select Your Leader *
                        </label>
                        {loadingLeaders ? (
                          <div className="flex items-center justify-center py-3">
                            <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                            <span className="ml-2 text-sm text-gray-300">Loading leaders...</span>
                          </div>
                        ) : availableLeaders.length === 0 ? (
                          <div className="p-3 bg-yellow-900/50 border border-yellow-700 rounded-lg">
                            <p className="text-sm text-yellow-200">
                              No leaders available for this role yet. 
                              {role === 'fsl' && ' A Satellite must register first.'}
                              {role === 'fta' && ' An FSL must register first.'}
                              {role === 'agent' && ' An FTA must register first.'}
                            </p>
                          </div>
                        ) : (
                          <div className="relative">
                            <select
                              value={leaderId}
                              onChange={(e) => setLeaderId(e.target.value)}
                              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none"
                              required={enableTeamMode && role !== 'satellite'}
                            >
                              <option value="">-- Select Leader --</option>
                              {availableLeaders.map((leader) => (
                                <option key={leader.id} value={leader.id}>
                                  {leader.name} ({leader.email})
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{isLogin ? 'Signing in...' : 'Building your system...'}</span>
                </>
              ) : (
                <span>{isLogin ? 'Sign In' : 'Build My System'}</span>
              )}
            </button>
          </form>

          {!isLogin && (
            <a
              href="#how-it-works"
              onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="mt-3 block w-full text-center text-sm text-blue-400 hover:text-blue-300 font-medium"
              data-testid="see-how-it-works"
            >
              See how it works →
            </a>
          )}

          {isLogin && (
            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-400 hover:text-blue-300 font-medium"
              >
                Forgot your password?
              </Link>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-blue-400 hover:text-blue-300 text-sm font-medium"
            >
              {isLogin 
                ? "Don't have an account? Sign up" 
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        <div id="how-it-works" className="mt-6 text-center text-sm text-gray-300">
          <p>Income hub · Automatic allocation · Weekly target · Rebalancing</p>
        </div>
      </div>
    </div>
  );
}

export default Auth;
